import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import type { JobContact, JobFilters, JobPublicRow } from "./jobs.types";

const filtersSchema = z.object({
  limit: z.number().int().min(1).max(100).optional(),
  q: z.string().trim().max(120).optional(),
  category: z.string().trim().max(60).optional(),
  city: z.string().trim().max(100).optional(),
  sort: z.enum(["newest", "oldest"]).optional(),
});

const slugSchema = z.object({ slug: z.string().trim().min(1).max(180) });
const revealSchema = z.object({ jobId: z.string().uuid() });

function publicJob(row: JobPublicRow): JobPublicRow {
  return {
    id: row.id,
    created_at: row.created_at,
    slug: row.slug,
    category: row.category,
    title: row.title,
    description: row.description,
    timeline: row.timeline,
    budget: row.budget,
    postal_code: row.postal_code,
    city: row.city,
    object_type: row.object_type,
    images: Array.isArray(row.images)
      ? row.images.filter((image) => typeof image === "string")
      : [],
    locale: row.locale,
    status: row.status,
    published_at: row.published_at,
    expires_at: row.expires_at,
  };
}

export const getPublicJobs = createServerFn({ method: "POST" })
  .validator((data: unknown) => filtersSchema.parse(data ?? {}))
  .handler(async ({ data }) => {
    const { drainJobAssetCleanup, fetchPublicJobs } = await import("./supabase.server");
    const cleanup = drainJobAssetCleanup(10);
    const { getRequest } = await import("@tanstack/react-start/server");
    const request = getRequest() as Request & {
      waitUntil?: (promise: Promise<unknown>) => void;
    };
    if (typeof request.waitUntil === "function") request.waitUntil(cleanup);
    else void cleanup.catch(() => undefined);

    const rows = await fetchPublicJobs(data as JobFilters);
    return { jobs: rows.map(publicJob) };
  });

export const getPublicJob = createServerFn({ method: "POST" })
  .validator((data: unknown) => slugSchema.parse(data))
  .handler(async ({ data }) => {
    const { fetchPublicJobBySlug } = await import("./supabase.server");
    const row = await fetchPublicJobBySlug(data.slug);
    return { job: row ? publicJob(row) : null };
  });

export const revealJobContact = createServerFn({ method: "POST" })
  .validator((data: unknown) => revealSchema.parse(data))
  .handler(async ({ data }) => {
    const [{ requireVerifiedContractor }, { fetchJobContact }] = await Promise.all([
      import("./auth.server"),
      import("./supabase.server"),
    ]);

    const access = await requireVerifiedContractor();
    const { privateJobResponse } = await import("./supabase-rpc.server");
    await privateJobResponse();

    const { getRequestHeader } = await import("@tanstack/react-start/server");
    const requestAddress =
      getRequestHeader("cf-connecting-ip") ??
      getRequestHeader("x-forwarded-for")?.split(",")[0]?.trim() ??
      "unknown";
    const contact = await fetchJobContact(data.jobId, access.userId, requestAddress);
    if (!contact) throw new Response("Kontakt nicht freigegeben", { status: 403 });

    const safeContact: JobContact = {
      first_name: contact.first_name,
      last_name: contact.last_name,
      email: contact.email,
      phone: contact.phone ?? null,
    };
    return { contact: safeContact };
  });
